import { baseApi } from './baseApi'
import { API_ENDPOINTS } from '@/constants'
import type {
  ApiResponse,
  PaginatedResponse,
  Notification,
  CreateNotificationData,
  NotificationFilters,
  NotificationStatistics,
  NotificationPreferences,
  AlertRule,
} from '@/types'

export const notificationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ===================================
    // Notification Queries
    // ===================================

    /**
     * Get all notifications with filters
     */
    getNotifications: builder.query<
      PaginatedResponse<Notification>,
      NotificationFilters | void
    >({
      query: (filters) => ({
        url: API_ENDPOINTS.NOTIFICATIONS.BASE,
        params: filters || {},
      }),
      transformResponse: (response: ApiResponse<PaginatedResponse<Notification>>) =>
        response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Notification' as const, id })),
              { type: 'Notification', id: 'LIST' },
            ]
          : [{ type: 'Notification', id: 'LIST' }],
    }),

    /**
     * Get notification by ID
     */
    getNotificationById: builder.query<Notification, string>({
      query: (id) => API_ENDPOINTS.NOTIFICATIONS.DETAIL(id),
      transformResponse: (response: ApiResponse<Notification>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Notification', id }],
    }),

    /**
     * Get notification statistics
     */
    getNotificationStatistics: builder.query<NotificationStatistics, void>({
      query: () => API_ENDPOINTS.NOTIFICATIONS.STATISTICS,
      transformResponse: (response: ApiResponse<NotificationStatistics>) => response.data,
      providesTags: [{ type: 'Notification', id: 'STATS' }],
    }),

    /**
     * Get notification preferences
     */
    getNotificationPreferences: builder.query<NotificationPreferences, void>({
      query: () => API_ENDPOINTS.NOTIFICATIONS.PREFERENCES,
      transformResponse: (response: ApiResponse<NotificationPreferences>) => response.data,
      providesTags: [{ type: 'NotificationPreferences', id: 'CURRENT' }],
    }),

    // ===================================
    // Notification Mutations
    // ===================================

    /**
     * Create a notification (usually system-generated, but can be manual)
     */
    createNotification: builder.mutation<Notification, CreateNotificationData>({
      query: (data) => ({
        url: API_ENDPOINTS.NOTIFICATIONS.BASE,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<Notification>) => response.data,
      invalidatesTags: [
        { type: 'Notification', id: 'LIST' },
        { type: 'Notification', id: 'STATS' },
      ],
    }),

    /**
     * Mark a notification as read
     */
    markNotificationAsRead: builder.mutation<Notification, string>({
      query: (id) => ({
        url: API_ENDPOINTS.NOTIFICATIONS.MARK_READ(id),
        method: 'PATCH',
      }),
      transformResponse: (response: ApiResponse<Notification>) => response.data,
      invalidatesTags: (_result, _error, id) => [
        { type: 'Notification', id },
        { type: 'Notification', id: 'LIST' },
        { type: 'Notification', id: 'STATS' },
      ],
    }),

    /**
     * Mark all notifications as read
     */
    markAllNotificationsAsRead: builder.mutation<void, void>({
      query: () => ({
        url: API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ,
        method: 'PATCH',
      }),
      invalidatesTags: [
        { type: 'Notification', id: 'LIST' },
        { type: 'Notification', id: 'STATS' },
      ],
    }),

    /**
     * Delete a notification
     */
    deleteNotification: builder.mutation<void, string>({
      query: (id) => ({
        url: API_ENDPOINTS.NOTIFICATIONS.DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Notification', id },
        { type: 'Notification', id: 'LIST' },
        { type: 'Notification', id: 'STATS' },
      ],
    }),

    /**
     * Update notification preferences
     */
    updateNotificationPreferences: builder.mutation<
      NotificationPreferences,
      Partial<NotificationPreferences>
    >({
      query: (data) => ({
        url: API_ENDPOINTS.NOTIFICATIONS.UPDATE_PREFERENCES,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<NotificationPreferences>) => response.data,
      invalidatesTags: [{ type: 'NotificationPreferences', id: 'CURRENT' }],
    }),

    // ===================================
    // Alert Rule Queries
    // ===================================

    /**
     * Get all alert rules
     */
    getAlertRules: builder.query<AlertRule[], { farmId?: string }>({
      query: (params) => ({
        url: API_ENDPOINTS.ALERT_RULES.BASE,
        params,
      }),
      transformResponse: (response: ApiResponse<AlertRule[]>) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'AlertRule' as const, id })),
              { type: 'AlertRule', id: 'LIST' },
            ]
          : [{ type: 'AlertRule', id: 'LIST' }],
    }),

    /**
     * Get alert rule by ID
     */
    getAlertRuleById: builder.query<AlertRule, string>({
      query: (id) => API_ENDPOINTS.ALERT_RULES.DETAIL(id),
      transformResponse: (response: ApiResponse<AlertRule>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'AlertRule', id }],
    }),

    /**
     * Get alert rules by farm ID
     */
    getAlertRulesByFarm: builder.query<AlertRule[], string>({
      query: (farmId) => API_ENDPOINTS.ALERT_RULES.BY_FARM(farmId),
      transformResponse: (response: ApiResponse<AlertRule[]>) => response.data,
      providesTags: (_result, _error, farmId) => [
        { type: 'AlertRule', id: `FARM_${farmId}` },
      ],
    }),

    // ===================================
    // Alert Rule Mutations
    // ===================================

    /**
     * Create an alert rule
     */
    createAlertRule: builder.mutation<AlertRule, Partial<AlertRule>>({
      query: (data) => ({
        url: API_ENDPOINTS.ALERT_RULES.CREATE,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<AlertRule>) => response.data,
      invalidatesTags: [{ type: 'AlertRule', id: 'LIST' }],
    }),

    /**
     * Update an alert rule
     */
    updateAlertRule: builder.mutation<AlertRule, { id: string; data: Partial<AlertRule> }>({
      query: ({ id, data }) => ({
        url: API_ENDPOINTS.ALERT_RULES.UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<AlertRule>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'AlertRule', id },
        { type: 'AlertRule', id: 'LIST' },
      ],
    }),

    /**
     * Toggle alert rule enabled/disabled
     */
    toggleAlertRule: builder.mutation<AlertRule, string>({
      query: (id) => ({
        url: API_ENDPOINTS.ALERT_RULES.TOGGLE(id),
        method: 'PATCH',
      }),
      transformResponse: (response: ApiResponse<AlertRule>) => response.data,
      invalidatesTags: (_result, _error, id) => [
        { type: 'AlertRule', id },
        { type: 'AlertRule', id: 'LIST' },
      ],
    }),

    /**
     * Delete an alert rule
     */
    deleteAlertRule: builder.mutation<void, string>({
      query: (id) => ({
        url: API_ENDPOINTS.ALERT_RULES.DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'AlertRule', id },
        { type: 'AlertRule', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  // Notification hooks
  useGetNotificationsQuery,
  useGetNotificationByIdQuery,
  useGetNotificationStatisticsQuery,
  useGetNotificationPreferencesQuery,
  useCreateNotificationMutation,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useDeleteNotificationMutation,
  useUpdateNotificationPreferencesMutation,
  // Alert rule hooks
  useGetAlertRulesQuery,
  useGetAlertRuleByIdQuery,
  useGetAlertRulesByFarmQuery,
  useCreateAlertRuleMutation,
  useUpdateAlertRuleMutation,
  useToggleAlertRuleMutation,
  useDeleteAlertRuleMutation,
} = notificationApi
